import { Analytics } from '@cobliteam/analytics';
import { Location } from '@cobliteam/common-types';
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import { computePositionRect, POSITION_TRANSITION, useChatWidgetPositionStore } from '@cobliteam/hooks/src/useChatWidget/store';
import { useTranslation } from '@cobliteam/i18n';
import { lazy } from '@cobliteam/lazy-with-retries';
import Button from '@cobliteam/ui/components/Button';
import Popover, { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePendingInstallScheduling } from '../../hooks/usePendingInstallScheduling';
import { COBLI_ACTION_EVENT, type CobliActionEventDetail } from '../../lib/cobliLinkActions';
import { useAIAssistant, useFloatingAIChat, useFloatingChat } from '../../store';

// The floating chat panel owns the full AI chat stack (Mastra client, streamdown,
// shiki, chart tools, react-markdown). AIChatFloatingButton itself mounts on every
// authenticated page via AppLayout, but the panel only appears when the user actually
// opens the mini-chat. Lazy so ~600 KB of chat infrastructure isn't in the entry.
const AIChatFloatingPanel = lazy(() => import('./components/AIChatFloatingPanel'));

const AIChatFloatingButton = () => {
  const { t } = useTranslation('ai');
  const { openIntercom, closeIntercom, hide, isFooterVisible } = useChatWidget();
  const position = useChatWidgetPositionStore((s) => s.position);
  const { isAISidepanelOpen } = useAIAssistant();
  const aiChatThreadIdRef = useRef(crypto.randomUUID());
  const { intercomOpen, setIntercomOpen } = useFloatingChat();
  const [popoverOpen, setPopoverOpen] = useState(false);

  /*
   * AE-2763 brings back the scheduling option next to Intercom — #11537 had collapsed the FAB into
   * a single Intercom button, orphaning the `floating_chat.ai_chat` copy.
   *
   * Offered to anyone who may act on scheduling (the CRM permission), with or without a pending
   * ticket: a user who wants to schedule should not have to wait for a ticket to exist before the
   * entry point appears. Without the permission the FAB stays a direct Intercom launcher, one click,
   * no menu to dismiss.
   */
  const scheduling = usePendingInstallScheduling();

  const { isOpen: aiChatOpen, initialMessage, close: closeAIChat } = useFloatingAIChat();

  const { pathname } = useLocation();
  const isAIPage = pathname === Location.AI || pathname.startsWith(`${Location.AI}/`);

  const positionStyle = useMemo(() => {
    const { right, bottom } = computePositionRect(position, isFooterVisible);
    return { right: `${right}px`, bottom: `${bottom}px`, transition: POSITION_TRANSITION };
  }, [position, isFooterVisible]);

  useEffect(() => {
    hide(true);
    return () => hide(false);
  }, [hide]);

  // When the AI agent emits a `#cobli-open-blip-chat` link, AIChatMarkdown
  // opens Blip and dispatches this event so the floating AI chat panel closes
  // itself in the same gesture (the user clicks once; the chat panel goes
  // away and the Blip window takes over).
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<CobliActionEventDetail>).detail;
      if (detail?.action === 'open-blip-chat' && aiChatOpen) {
        Analytics.track('AI - Floating Chat - Closed', { component: 'floating-chat-button', trigger: 'ai-link-action' });
        closeAIChat();
      }
    };
    window.addEventListener(COBLI_ACTION_EVENT, handler);
    return () => window.removeEventListener(COBLI_ACTION_EVENT, handler);
  }, [aiChatOpen, closeAIChat]);

  const isAnyChatOpen = aiChatOpen || intercomOpen;

  const handleCloseAIChat = (trigger: 'fab-toggle' | 'panel-close-button') => {
    if (!aiChatOpen) return;
    Analytics.track('AI - Floating Chat - Closed', { component: 'floating-chat-button', trigger });
    closeAIChat();
  };

  const closeAllChats = () => {
    if (intercomOpen) {
      closeIntercom();
      setIntercomOpen(false);
    }
    handleCloseAIChat('fab-toggle');
  };

  const handleButtonClick = () => {
    if (isAnyChatOpen) {
      closeAllChats();
      return;
    }
    // With the scheduling option available the Popover takes over the click; without it, the
    // button stays a direct Intercom launcher so the common case keeps costing one click.
    if (scheduling.canOpenAgent) return;
    openIntercom();
    setIntercomOpen(true);
  };

  const handleIntercomOption = () => {
    openIntercom();
    setIntercomOpen(true);
    setPopoverOpen(false);
  };

  const handleScheduleInstallationOption = () => {
    // `useSchedulingAgent` emits the canonical `AI - Floating Chat - Opened` with `entryPoint`, so
    // tracking here too would double-count the open.
    scheduling.open('fab');
    setPopoverOpen(false);
  };

  // Mirror AISidepanelStateSync's behavior on the chat widget: when the
  // AI sidepanel takes over the right side, the floating chat (and the agent
  // panel inside it) hide so they don't overlap. State is preserved via zustand
  // — when the sidepanel closes, this remounts and any open chat is restored.
  // The full-page AI chat (/ai and its subpages) already exposes the assistant,
  // so the floating button would be redundant there.
  if (isAISidepanelOpen || isAIPage) return null;

  // Only offer a menu when there is a second thing to choose; `bot` signals "more than support".
  const showOptions = scheduling.canOpenAgent && !isAnyChatOpen;
  const fabIcon = showOptions ? 'bot' : 'headset';
  const fabAriaLabel = isAnyChatOpen ? t('floating_chat.close_aria_label') : showOptions ? t('floating_chat.open_aria_label') : t('floating_chat.cobli_support');

  return (
    <>
      <div data-testid='floating-chat-button' className='fixed z-1001' style={positionStyle}>
        <Popover open={showOptions ? popoverOpen : false} onOpenChange={showOptions ? setPopoverOpen : undefined}>
          <PopoverTrigger asChild>
            <Button
              data-testid='intercom-launcher'
              variant='contained'
              color='primary'
              icon={<Icon name={isAnyChatOpen ? 'x' : fabIcon} size='md' />}
              className='mr-2 h-14 w-14 rounded-full shadow-lg'
              onClick={handleButtonClick}
              aria-label={fabAriaLabel}
            />
          </PopoverTrigger>
          <PopoverContent align='end' className='mb-2 w-64 p-1'>
            <button
              data-testid='intercom-option'
              type='button'
              className='flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-gray-2'
              onClick={handleIntercomOption}
            >
              <Icon name='headset' size='sm' className='text-gray-9' />
              <span>{t('floating_chat.cobli_support')}</span>
            </button>
            {/* The label names installation only, but the agent also handles uninstallation and
                answers questions about existing schedulings — the tooltip carries what the label
                cannot without getting long. */}
            <Tooltip content={t('floating_chat.ai_chat_tooltip')} position='left'>
              <button
                data-testid='schedule-installation-option'
                type='button'
                className='flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-gray-2'
                onClick={handleScheduleInstallationOption}
              >
                <Icon name='sparkles' size='sm' className='text-primary-9' />
                <span>{t('floating_chat.ai_chat')}</span>
              </button>
            </Tooltip>
          </PopoverContent>
        </Popover>
      </div>

      {aiChatOpen && (
        <Suspense fallback={null}>
          <AIChatFloatingPanel onClose={() => handleCloseAIChat('panel-close-button')} initialMessage={initialMessage} threadId={aiChatThreadIdRef.current} />
        </Suspense>
      )}
    </>
  );
};

export default AIChatFloatingButton;
